import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Mountain } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { requestPasswordReset } from '../lib/api/auth'

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setMessage(null)
    setError(null)

    try {
      const response = await requestPasswordReset(email.trim().toLowerCase())
      setMessage(response.message)
    } catch (err: unknown) {
      console.error(err)
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { message?: string } } }).response
        setError(response?.data?.message ?? 'Unable to send password reset email.')
      } else {
        setError('Unable to send password reset email.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background topo-pattern p-8">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg">
            <Mountain className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent">HikeStrong</p>
            <h2 className="text-xl font-bold tracking-tight text-foreground">Base Camp</h2>
          </div>
        </div>

        <div className="elevation-card rounded-2xl p-8">
          <div className="mb-8">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-1 w-8 rounded-full bg-accent"></div>
              <span className="font-mono text-xs font-bold uppercase tracking-wider text-accent">
                Account Recovery
              </span>
            </div>
            <h1 className="text-3xl font-bold text-foreground">Reset Password</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Enter your dashboard email and we will send a secure reset link.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="forgot-email" className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address
              </label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="guide@hikestrong.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-12"
                required
              />
            </div>

            {message && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm font-medium text-emerald-700">{message}</p>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
